import React from 'react';
import { Card, Col, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import PropTypes from 'prop-types';

import { ArrowUpOutlined, TrophyOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

export default function SimulationResults({ results }) {
    const { baseRPercentages, simulations, tradeCount } = results;

    // Find the best performing plan
    const bestPlan = simulations.reduce(
        (best, current) =>
            current.totalExpectedProfit > best.totalExpectedProfit ? current : best,
        simulations[0]
    );

    const columns = [
        {
            title: 'Plan',
            dataIndex: 'planName',
            key: 'planName',
            render: (text, record) => (
                <Space>
                    <Text strong>{text}</Text>
                    {record.planName === bestPlan.planName && (
                        <Tag icon={<TrophyOutlined />} color="gold">
                            Best
                        </Tag>
                    )}
                </Space>
            )
        },
        {
            title: 'Strategy',
            dataIndex: 'partials',
            key: 'partials',
            render: (partials) => (
                <Text type="secondary">
                    {partials.map((p) => `${p.percentage}% @ ${p.rLevel}R`).join(', ')}
                </Text>
            )
        },
        {
            title: 'Expected R/Trade',
            dataIndex: 'expectedProfitPerTrade',
            key: 'expectedProfitPerTrade',
            render: (val) => (
                <Text strong style={{ color: val > 0 ? '#52c41a' : '#ff4d4f' }}>
                    {val.toFixed(3)}R
                </Text>
            )
        },
        {
            title: `Total (${tradeCount} trades)`,
            dataIndex: 'totalExpectedProfit',
            key: 'totalExpectedProfit',
            render: (val) => (
                <Statistic
                    value={val}
                    precision={2}
                    suffix="R"
                    valueStyle={{
                        color: val > 0 ? '#52c41a' : '#ff4d4f',
                        fontSize: 16
                    }}
                    prefix={val > 0 ? <ArrowUpOutlined /> : null}
                />
            )
        }
    ];

    return (
        <div className="simulation-results">
            <Title level={5} style={{ marginTop: 24 }}>
                Simulation Results
            </Title>

            {/* Summary Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Best Strategy"
                            value={bestPlan.planName}
                            valueStyle={{ fontSize: 16 }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Best Expected Profit"
                            value={bestPlan.totalExpectedProfit}
                            precision={2}
                            suffix="R"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                        <Statistic title="Trade Count" value={tradeCount} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="1R Hit Rate"
                            value={baseRPercentages['1R']}
                            precision={1}
                            suffix="%"
                        />
                    </Card>
                </Col>
            </Row>

            {/* Results Table */}
            <Table
                columns={columns}
                dataSource={simulations.map((s, i) => ({ ...s, key: i }))}
                pagination={false}
                size="small"
            />

            {/* Detailed Breakdown */}
            <Card size="small" title="R-Level Hit Rates" style={{ marginTop: 16 }}>
                <Row gutter={[16, 8]}>
                    {Object.entries(baseRPercentages).map(([level, pct]) => (
                        <Col xs={8} sm={4} key={level}>
                            <Statistic
                                title={level}
                                value={pct}
                                precision={1}
                                suffix="%"
                                valueStyle={{ fontSize: 14 }}
                            />
                        </Col>
                    ))}
                </Row>
            </Card>
        </div>
    );
}

SimulationResults.propTypes = {
    results: PropTypes.shape({
        baseRPercentages: PropTypes.objectOf(PropTypes.number).isRequired,
        simulations: PropTypes.arrayOf(
            PropTypes.shape({
                planName: PropTypes.string.isRequired,
                partials: PropTypes.arrayOf(
                    PropTypes.shape({
                        rLevel: PropTypes.number.isRequired,
                        percentage: PropTypes.number.isRequired
                    })
                ).isRequired,
                expectedProfitPerTrade: PropTypes.number.isRequired,
                totalExpectedProfit: PropTypes.number.isRequired
            })
        ).isRequired,
        tradeCount: PropTypes.number.isRequired
    }).isRequired
};
